import { useDashboardContext } from '@/contexts/DashboardContext';
import { useSnackbarContext } from '@/contexts/SnackbarContext';
import { UserContext } from '@/contexts/UserContext';
import CopyDynamicPageLinkModal from '@/modals/CopyDynamicPageLinkModal';
import { getPrevDynamicPageVersion } from '@/services/DynamicPagesService';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Popover,
  PopoverPanelBody,
  PopoverPanelContent,
  PopoverPanelFooter,
  PopoverPanelHeader,
  PopoverPanelTitle,
  PopoverTrigger,
} from '@superfunnel/ui-core/v2/PopoverPanel';
import { CheckIcon, ChevronDownIcon, CopyIcon, LinkIcon } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getDynamicPagePrimaryDomain,
  getDynamicPageRelevantDomains,
} from '../../utils';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';

interface PublishPopoverProps {
  hasChanges: boolean;
  saving: boolean;
  publishing: boolean;
  discarding: boolean;
  onPublish: () => Promise<void>;
}

export const PublishPopover = ({
  hasChanges,
  saving,
  publishing,
  discarding,
  onPublish,
}: PublishPopoverProps) => {
  const { showError } = useSnackbarContext();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyLinkModalOpen, setCopyLinkModalOpen] = useState(false);
  const [lastPublishedAt, setLastPublishedAt] = useState<Date | null>(null);
  const [loadingVersion, setLoadingVersion] = useState(false);

  const { data } = useHtmlStepperEditor();
  const { quiz, dynamicPage, dynamicPageId } = data;
  const { selectedAccount } = useContext(UserContext);
  const { domains } = useDashboardContext();

  const isLive = quiz?.isLive ?? false;
  const hasUnpublishedChanges = quiz?.hasChangesToPublish ?? false;

  // Get relevant domains for the dynamic page
  const relevantDomains = useMemo(() => {
    if (!domains || !dynamicPage) return domains;
    return getDynamicPageRelevantDomains(domains, dynamicPage);
  }, [domains, dynamicPage]);

  const primaryDomain = useMemo(() => {
    if (!relevantDomains) return;
    return getDynamicPagePrimaryDomain(relevantDomains);
  }, [relevantDomains]);

  // Build the URL
  const pageUrl = useMemo(() => {
    if (!primaryDomain || !dynamicPage) return null;
    return `https://${primaryDomain.data().domain}/${dynamicPage.path}`;
  }, [primaryDomain, dynamicPage]);

  // Fetch version when popover opens
  useEffect(() => {
    if (!open) return;
    if (!selectedAccount || !dynamicPageId) return;

    setLoadingVersion(true);
    getPrevDynamicPageVersion(selectedAccount.id, dynamicPageId)
      .then((docs) => {
        const latestVersion = docs?.[0] ?? null;
        if (latestVersion) {
          const createdAt = latestVersion.data().createdAt;
          // Handle Firestore Timestamp
          const date =
            createdAt && typeof createdAt.toDate === 'function'
              ? createdAt.toDate()
              : createdAt instanceof Date
                ? createdAt
                : null;
          setLastPublishedAt(date);
        } else {
          setLastPublishedAt(null);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch version:', err);
        setLastPublishedAt(null);
      })
      .finally(() => {
        setLoadingVersion(false);
      });
  }, [open, selectedAccount, dynamicPageId]);

  // Format relative time for last published
  const formatRelativeTime = useCallback((date: Date | null) => {
    if (!date) return null;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHour / 24);

    if (diffSec < 60) {
      return 'just now';
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }, []);

  const handleCopy = async () => {
    if (!pageUrl) return;
    await navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePublish = async () => {
    await onPublish().catch((err) => {
      console.error('Publish failed:', err);
      showError(err);
    });
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant={hasChanges ? 'default' : 'outline'}
            disabled={saving || discarding || publishing}
            loading={publishing}
          >
            Publish
            <ChevronDownIcon className="ml-1 size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverPanelContent width="sm" align="end">
          <PopoverPanelHeader>
            <PopoverPanelTitle>Publish</PopoverPanelTitle>
          </PopoverPanelHeader>

          <PopoverPanelBody className="flex flex-col gap-4">
            {/* Empty state - never published */}
            {!isLive && (
              <div className="text-base-500 py-2 text-center text-sm">
                This quiz has never been published.
                <br />
                Publish to make it live.
              </div>
            )}

            {/* Published state - show status and URL */}
            {isLive && (
              <>
                {/* Published status */}
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center gap-0.5 text-xs">
                    <span className="font-medium">Last published:</span>
                    {loadingVersion ? (
                      <div className="bg-base-100 h-4 w-24 animate-pulse rounded" />
                    ) : (
                      <span className="text-base-400">
                        {lastPublishedAt
                          ? formatRelativeTime(lastPublishedAt)
                          : 'Unknown'}
                      </span>
                    )}
                  </div>

                  {hasUnpublishedChanges && (
                    <Badge variant="blue" className="mt-1">
                      Unpublished Changes
                    </Badge>
                  )}
                </div>

                {/* URL section */}
                {pageUrl && (
                  <div className="flex flex-col gap-2">
                    <div className="border-base-200 hover:border-base-300 flex h-10 w-full items-center rounded-lg border bg-white pr-1.5 pl-3 shadow-xs transition-all">
                      <a
                        href={pageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-600 hover:text-brand-700 flex-1 truncate text-sm tracking-wide underline-offset-2 hover:underline"
                        title={pageUrl}
                      >
                        {pageUrl}
                      </a>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleCopy}
                        className="ml-2 shrink-0"
                      >
                        {copied ? (
                          <CheckIcon className="size-4 text-green-600" />
                        ) : (
                          <CopyIcon className="text-base-500 size-4" />
                        )}
                      </Button>
                    </div>

                    {/* Manage URLs link */}
                    <button
                      type="button"
                      onClick={() => setCopyLinkModalOpen(true)}
                      className="text-brand-600 hover:text-brand-700 flex items-center gap-1 text-xs underline-offset-2 hover:underline"
                    >
                      <LinkIcon className="size-3" />
                      Add to Ads Account
                    </button>
                  </div>
                )}
              </>
            )}
          </PopoverPanelBody>

          <PopoverPanelFooter>
            <Button
              size="sm"
              onClick={handlePublish}
              loading={publishing}
              disabled={publishing || saving}
              className="w-full"
            >
              {isLive ? 'Publish Changes' : 'Publish'}
            </Button>
          </PopoverPanelFooter>
        </PopoverPanelContent>
      </Popover>

      <CopyDynamicPageLinkModal
        relevantDomains={relevantDomains}
        primaryDomain={primaryDomain}
        dynamicPage={dynamicPage}
        open={copyLinkModalOpen}
        onClose={() => setCopyLinkModalOpen(false)}
      />
    </>
  );
};
